import { useCallback, useLayoutEffect, useRef } from "react";
import type { ReactNode, RefObject, UIEvent } from "react";
import { observer } from "mobx-react-lite";
import { Empty, Tooltip } from "antd";
import {
  CopyOutlined,
  DeleteOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  AppearanceSection,
  ColorsSection,
  MediaSection,
  OrientationSection,
  PathSection,
  RevealPathSection,
  TextSection,
} from "./LayerVisualProperties/sections";
import {
  DocumentColorsSection,
  DocumentGeneralSection,
  DocumentMediaSection,
} from "./LayerVisualProperties/documentSections";
import { useLayerVisualPropertiesPanelModel } from "./LayerVisualProperties/useLayerVisualPropertiesPanelModel";
import { formatLayerKind } from "./LayerVisualProperties/utils";
import { useLayerStructuralActions } from "./useLayerStructuralActions";
import { useMst } from "../../stores/main";
import { recordStudioPerf, studioPerfNow } from "../../helpers/studioPerfDebug";
import "./LayerVisualPropertiesPanel.scss";

const layerPanelScrollPositions = new Map<string, number>();

const getLayerPanelScrollKey = (
  target: {
    assetId?: string;
    layerName?: string;
    layerUid?: number;
  } | null
): string | null => {
  if (!target?.layerName) {
    return null;
  }

  return [
    target.assetId ?? "main",
    target.layerUid ?? "no-uid",
    target.layerName,
  ].join("::");
};

type LayerVisualPropertiesPanelShellProps = {
  ariaLabel: string;
  body?: ReactNode;
  canUseLayerActions?: boolean;
  isApplyingLayerAction?: boolean;
  isCollapsed: boolean;
  isDocumentPanel: boolean;
  onDelete?: () => void;
  onDuplicate?: () => void;
  onToggleCollapsed: () => void;
  panelRef?: RefObject<HTMLElement | null>;
  panelSubtitle?: string;
  panelTitle?: string;
};

const LayerVisualPropertiesPanelShell = ({
  ariaLabel,
  body,
  canUseLayerActions = false,
  isApplyingLayerAction = false,
  isCollapsed,
  isDocumentPanel,
  onDelete,
  onDuplicate,
  onToggleCollapsed,
  panelRef,
  panelSubtitle,
  panelTitle,
}: LayerVisualPropertiesPanelShellProps) => (
  <aside
    ref={panelRef}
    className={`ModForm layer-visual-panel${
      isCollapsed ? " layer-visual-panel--collapsed" : ""
    }`}
    aria-label={ariaLabel}
    data-layer-inspector-keep-selection
    onPointerDown={(event) => event.stopPropagation()}
  >
    <div className="layer-visual-panel__header">
      <div className="layer-visual-panel__header-text">
        <div className="layer-visual-panel__title">
          <span className="layer-visual-panel__title-name">{panelTitle}</span>
          {panelSubtitle ? (
            <span className="layer-visual-panel__title-type">
              {panelSubtitle}
            </span>
          ) : null}
        </div>
      </div>
      <div className="layer-visual-panel__header-actions">
        {!isDocumentPanel && onDuplicate && onDelete && (
          <>
            <Tooltip title="Duplicate layer" placement="bottom">
              <button
                type="button"
                className="layer-visual-panel__action-button"
                aria-label="Duplicate layer"
                disabled={!canUseLayerActions || isApplyingLayerAction}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onDuplicate();
                }}
              >
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title="Delete layer" placement="bottom">
              <button
                type="button"
                className="layer-visual-panel__action-button layer-visual-panel__action-button--danger"
                aria-label="Delete layer"
                disabled={!canUseLayerActions || isApplyingLayerAction}
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete();
                }}
              >
                <DeleteOutlined />
              </button>
            </Tooltip>
          </>
        )}
        <button
          type="button"
          className="layer-visual-panel__collapse-toggle"
          aria-label={
            isCollapsed
              ? `Open ${isDocumentPanel ? "scene" : "layer"} panel`
              : `Close ${isDocumentPanel ? "scene" : "layer"} panel`
          }
          aria-expanded={!isCollapsed}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onToggleCollapsed();
          }}
        >
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </button>
      </div>
    </div>

    {body}
  </aside>
);

export const LayerVisualPropertiesPanel = observer(() => {
  const renderStartedAt = studioPerfNow();
  const {
    layoutStore: {
      layerVisualPanelCollapsed: isCollapsed,
      setLayerVisualPanelCollapsed,
    },
    platformStore: { selectedVideoPartName },
    playerStore: { layerInspectorPrimarySelection, layerInspectorSelections },
  } = useMst();

  if (!isCollapsed) {
    return (
      <ExpandedLayerVisualPropertiesPanel
        setLayerVisualPanelCollapsed={setLayerVisualPanelCollapsed}
      />
    );
  }

  const selectionCount = layerInspectorSelections.length;
  const isDocumentPanel = selectionCount === 0;
  const panelTitle = isDocumentPanel
    ? "Scene"
    : selectionCount > 1
    ? `${selectionCount} layers`
    : layerInspectorPrimarySelection?.layerName ?? "Layer";
  const panelSubtitle = isDocumentPanel
    ? selectedVideoPartName ?? undefined
    : undefined;

  recordStudioPerf(
    "LayerVisualPropertiesPanel render",
    studioPerfNow() - renderStartedAt,
    {
      documentPanel: isDocumentPanel,
      hasCapabilities: false,
      isCollapsed,
      isUsingCachedLayerSelection: false,
      selectionCount,
    }
  );

  return (
    <LayerVisualPropertiesPanelShell
      ariaLabel={
        isDocumentPanel ? "Scene properties" : "Layer visual properties"
      }
      isCollapsed
      isDocumentPanel={isDocumentPanel}
      onToggleCollapsed={() => setLayerVisualPanelCollapsed(false)}
      panelSubtitle={panelSubtitle}
      panelTitle={panelTitle}
    />
  );
});

const ExpandedLayerVisualPropertiesPanel = observer(
  ({
    setLayerVisualPanelCollapsed,
  }: {
    setLayerVisualPanelCollapsed: (value: boolean) => void;
  }) => {
    const renderStartedAt = studioPerfNow();
    const isCollapsed = false;
    const panelRef = useRef<HTMLElement | null>(null);
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const {
      target,
      capabilities,
      editableFields,
      textContentField,
      fontOptions,
      layerFont,
      mediaControls,
      uploadAssetToProject,
      applyFieldValue,
      removeFieldValue,
      freezeAnimatedFields,
      alignLayer,
      applyAnchorPosition,
      replaceMedia,
      replaceLayerFont,
      activeAnchorPosition,
      activeTextDocumentIndex,
      activeTextDocumentStyle,
      canAddFill,
      canAddOutline,
      hasCuratedFields,
      selectionCount,
      isLayerInspectorAvailable,
      isUsingCachedLayerSelection,
      sceneName,
      fieldConnectionActions,
      openPropertyConnector,
      documentSummary,
      documentColorReport,
      documentColorStatus,
      documentMediaAssets,
      applyDocumentSize,
      applyDocumentDuration,
      replaceDocumentColor,
      replaceDocumentMedia,
    } = useLayerVisualPropertiesPanelModel();
    const {
      deleteLayers,
      duplicateLayers,
      isApplyingLayerAction,
    } = useLayerStructuralActions();
    const isDocumentPanel = selectionCount === 0 && !target;
    const isMultiLayerSelection = !isDocumentPanel && selectionCount > 1;
    const fallbackPropertyTarget = isMultiLayerSelection
      ? undefined
      : capabilities?.target;
    const shapeSizeField =
      editableFields.get("rectangleSize") ?? editableFields.get("ellipseSize");
    const isLayerSelectionPending =
      !isDocumentPanel &&
      isUsingCachedLayerSelection &&
      !isLayerInspectorAvailable;
    const scrollKey = isDocumentPanel
      ? "document"
      : getLayerPanelScrollKey(target);
    const canPersistPanelScroll = isDocumentPanel || Boolean(capabilities);
    const canUseLayerActions =
      !isDocumentPanel && isLayerInspectorAvailable && selectionCount >= 1;
    const handleBodyScroll = useCallback(
      (event: UIEvent<HTMLDivElement>) => {
        if (!scrollKey || !canPersistPanelScroll) return;
        layerPanelScrollPositions.set(scrollKey, event.currentTarget.scrollTop);
      },
      [canPersistPanelScroll, scrollKey]
    );

    useLayoutEffect(() => {
      if (!scrollKey || !canPersistPanelScroll || !bodyRef.current) return;

      const savedScrollTop = layerPanelScrollPositions.get(scrollKey);
      if (typeof savedScrollTop !== "number") return;

      const body = bodyRef.current;
      const maxScrollTop = Math.max(0, body.scrollHeight - body.clientHeight);
      const nextScrollTop = Math.min(savedScrollTop, maxScrollTop);
      if (Math.abs(body.scrollTop - nextScrollTop) > 1) {
        body.scrollTop = nextScrollTop;
      }
    });

    useLayoutEffect(() => {
      const body = bodyRef.current;

      return () => {
        if (!scrollKey || !canPersistPanelScroll || !body) return;
        layerPanelScrollPositions.set(scrollKey, body.scrollTop);
      };
    }, [canPersistPanelScroll, scrollKey]);

    if (!target && !isDocumentPanel) {
      return null;
    }

    let panelBody: ReactNode = null;
    if (!isCollapsed) {
      panelBody = isLayerSelectionPending ? (
        <div
          ref={bodyRef}
          className="layer-visual-panel__body"
          onScroll={handleBodyScroll}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Layer inspector is reconnecting"
          />
        </div>
      ) : isDocumentPanel ? (
        <div
          ref={bodyRef}
          className="layer-visual-panel__body"
          onScroll={handleBodyScroll}
        >
          {documentSummary ? (
            <>
              <DocumentGeneralSection
                summary={documentSummary}
                onSizeChange={applyDocumentSize}
                onDurationChange={applyDocumentDuration}
              />
              <DocumentColorsSection
                colors={documentColorReport?.colors}
                status={documentColorStatus}
                onOpenConnector={openPropertyConnector}
                onReplaceColor={replaceDocumentColor}
              />
              <DocumentMediaSection
                assets={documentMediaAssets}
                onReplaceMedia={replaceDocumentMedia}
                uploadAssetsToProject={uploadAssetToProject}
              />
            </>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No scene loaded"
            />
          )}
        </div>
      ) : !capabilities ? (
        <div
          ref={bodyRef}
          className="layer-visual-panel__body"
          onScroll={handleBodyScroll}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="No editable layer properties"
          />
        </div>
      ) : (
        <div
          ref={bodyRef}
          className="layer-visual-panel__body"
          onScroll={handleBodyScroll}
        >
          <OrientationSection
            fields={editableFields}
            onChange={applyFieldValue}
            onFreeze={freezeAnimatedFields}
            canAlign={capabilities.canAlign}
            onAlign={alignLayer}
            onAnchorPositionChange={applyAnchorPosition}
            activeAnchorPosition={activeAnchorPosition}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />
          <TextSection
            fields={editableFields}
            onChange={applyFieldValue}
            onRemove={removeFieldValue}
            onFreeze={freezeAnimatedFields}
            layerKind={capabilities.layerKind}
            target={fallbackPropertyTarget}
            fontOptions={fontOptions}
            currentFontValue={layerFont?.fontKey ?? layerFont?.fontName}
            onFontChange={replaceLayerFont}
            textContentField={textContentField}
            activeTextDocumentIndex={activeTextDocumentIndex}
            activeTextDocumentStyle={activeTextDocumentStyle}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />
          <PathSection
            field={editableFields.get("shapePath")}
            sizeField={shapeSizeField}
            roundCornerField={editableFields.get("rectangleRoundness")}
            onChange={applyFieldValue}
            onFreeze={freezeAnimatedFields}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />
          <ColorsSection
            fields={editableFields}
            onChange={applyFieldValue}
            onRemove={removeFieldValue}
            onFreeze={freezeAnimatedFields}
            layerKind={capabilities.layerKind}
            target={fallbackPropertyTarget}
            canAddFill={canAddFill}
            canAddOutline={canAddOutline}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />
          <MediaSection
            mediaReplacement={capabilities.mediaReplacement}
            target={capabilities.target}
            controls={mediaControls}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
            onReplaceMedia={replaceMedia}
            uploadAssetsToProject={uploadAssetToProject}
          />
          <AppearanceSection
            fields={editableFields}
            onChange={applyFieldValue}
            onFreeze={freezeAnimatedFields}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />
          <RevealPathSection
            fields={editableFields}
            onChange={applyFieldValue}
            onFreeze={freezeAnimatedFields}
            connectionActions={fieldConnectionActions}
            onOpenConnector={openPropertyConnector}
          />

          {!hasCuratedFields && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No visual properties for this layer"
            />
          )}
        </div>
      );
    }

    let panelTitle = capabilities?.target.layerName ?? target?.layerName;
    if (isDocumentPanel) {
      panelTitle = "Scene";
    } else if (isMultiLayerSelection) {
      panelTitle = `${selectionCount} layers`;
    }

    let panelSubtitle: string | undefined;
    if (isDocumentPanel) {
      panelSubtitle = sceneName;
    } else if (!isMultiLayerSelection && capabilities) {
      panelSubtitle = formatLayerKind(capabilities.layerKind);
    }

    recordStudioPerf(
      "LayerVisualPropertiesPanel render",
      studioPerfNow() - renderStartedAt,
      {
        documentPanel: isDocumentPanel,
        hasCapabilities: Boolean(capabilities),
        isCollapsed,
        isUsingCachedLayerSelection,
        selectionCount,
      }
    );

    return (
      <LayerVisualPropertiesPanelShell
        ariaLabel={
          isDocumentPanel ? "Scene properties" : "Layer visual properties"
        }
        body={panelBody}
        canUseLayerActions={canUseLayerActions}
        isApplyingLayerAction={isApplyingLayerAction}
        isCollapsed={isCollapsed}
        isDocumentPanel={isDocumentPanel}
        onDelete={deleteLayers}
        onDuplicate={duplicateLayers}
        onToggleCollapsed={() => setLayerVisualPanelCollapsed(true)}
        panelRef={panelRef}
        panelSubtitle={panelSubtitle}
        panelTitle={panelTitle}
      />
    );
  }
);
